import { useState, useEffect } from "react";
import { ThreeEvent } from "@react-three/fiber";
import { Line } from "@react-three/drei";
import { message } from "antd";
import * as THREE from "three";

import { Pt } from "../../utils/apolloRenderer";

/**
 * 경로 편집 오버레이(설계 §6.3, §4.2) — `<Canvas>` 내부에 배치.
 * z=0 투명 지면 mesh 로 클릭 피킹(e.point 는 씬 로컬 미터, z-up → 변환 불필요).
 * 클릭 즉시 캐시된 맵의 차선/경계선과의 근접을 검사해(도로 밖이면 빨간 표시+경고, 웨이포인트 미추가),
 * 전송 후 실패 대신 클릭 순간에 유효/무효 피드백을 준다(Dreamview CheckRoutingPoint 근사).
 * 웨이포인트 핀(경유=청록, 목적지=빨강) + 미리보기 폴리라인(차량 시작점 → 경유 → 목적지).
 */

const CYAN = "#39d0ff";
const RED = "#ff4d4f";
const INVALID = "#ff2d2d";
const LANE_THRESHOLD_M = 6; // 차선/경계선에서 이 거리 이내면 유효(전송은 Dreamview 가 최종 검증)

// 점(px,py)에서 선분(a→b)까지의 최소 거리
function distToSeg(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let t = len2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx, cy = ay + t * dy;
    return Math.hypot(px - cx, py - cy);
}

// 점에서 가장 가까운 도로 폴리라인까지의 거리(임계값 미만이면 조기 종료)
function nearestRoadDist(x: number, y: number, lines: Pt[][]): number {
    let best = Infinity;
    for (const line of lines) {
        for (let i = 0; i < line.length - 1; i++) {
            const d = distToSeg(x, y, line[i][0], line[i][1], line[i + 1][0], line[i + 1][1]);
            if (d < best) { best = d; if (best <= LANE_THRESHOLD_M) return best; }
        }
    }
    return best;
}

export default function RouteEditor({
    waypoints,
    addWaypoint,
    startXY,
    roadLines,
}: {
    waypoints: Pt[];
    addWaypoint: (x: number, y: number) => void;
    startXY: [number, number] | null;   // 차량 현재 위치(미리보기 시작점)
    roadLines: Pt[][];                   // 근접 검증용 차선/경계선 폴리라인(로컬)
}) {
    const [invalid, setInvalid] = useState<{ x: number; y: number; key: number } | null>(null);

    // 무효 표시는 잠깐만 보여주고 자동 소거
    useEffect(() => {
        if (!invalid) return;
        const t = setTimeout(() => setInvalid(null), 1500);
        return () => clearTimeout(t);
    }, [invalid]);

    const onGroundClick = (e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        const x = e.point.x, y = e.point.y;
        // 맵을 아직 못 받았으면 검증 생략(막지 않음)
        if (roadLines.length > 0 && nearestRoadDist(x, y, roadLines) > LANE_THRESHOLD_M) {
            setInvalid({ x, y, key: Date.now() });
            message.warning({ content: "차선(도로) 위를 클릭하세요", key: "route-invalid", duration: 1.5 });
            return;
        }
        addWaypoint(x, y);
    };

    const n = waypoints.length;

    // 미리보기 폴리라인: 차량 시작점 → 웨이포인트들
    const linePts: [number, number, number][] = [];
    if (startXY) linePts.push([startXY[0], startXY[1], 0.2]);
    waypoints.forEach((w) => linePts.push([w[0], w[1], 0.2]));

    return (
        <group>
            {/* 투명 지면 피킹 평면(XY, 법선 +Z) — 대반경 맵도 끝까지 클릭되도록 크게 */}
            <mesh onClick={onGroundClick} position={[0, 0, 0]}>
                <planeGeometry args={[20000, 20000]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
            </mesh>

            {/* 미리보기 경로 */}
            {linePts.length >= 2 && (
                <Line points={linePts} color={CYAN} lineWidth={2} dashed dashSize={1.5} gapSize={0.8} transparent opacity={0.9} />
            )}

            {/* 무효(도로 밖) 클릭 표시 — 빨간 X 링 */}
            {invalid && (
                <group key={invalid.key} position={[invalid.x, invalid.y, 0]}>
                    <mesh position={[0, 0, 0.03]}>
                        <ringGeometry args={[0.9, 1.3, 24]} />
                        <meshBasicMaterial color={INVALID} transparent opacity={0.85} depthWrite={false} side={THREE.DoubleSide} />
                    </mesh>
                    <mesh position={[0, 0, 0.9]}>
                        <sphereGeometry args={[0.55, 12, 12]} />
                        <meshBasicMaterial color={INVALID} />
                    </mesh>
                </group>
            )}

            {/* 웨이포인트 핀 */}
            {waypoints.map((w, i) => {
                const isDest = i === n - 1;
                const color = isDest ? RED : CYAN;
                return (
                    <group key={i} position={[w[0], w[1], 0]}>
                        {/* 지면 마커(XY 원판) */}
                        <mesh position={[0, 0, 0.02]}>
                            <circleGeometry args={[0.55, 24]} />
                            <meshBasicMaterial color={color} transparent opacity={0.55} depthWrite={false} />
                        </mesh>
                        {/* 부양 구 */}
                        <mesh position={[0, 0, isDest ? 1.3 : 1.0]}>
                            <sphereGeometry args={[isDest ? 0.7 : 0.55, 16, 16]} />
                            <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.5} />
                        </mesh>
                    </group>
                );
            })}
        </group>
    );
}
